import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { businessCostsService, settingsService, reportsService, staffService, costsService, jobsService } from '../services/api';
import { formatMoney } from '../utils/pricing';
import { todayBrisbane } from '../utils/costs';
import { EXPENSE_CATEGORIES, EVERY_MONTHS, LIFE_BASES, LOAN_STATUS, categoryLabel, everyLabel, annualised, equipmentRate, fmtDate, fmtNum } from '../utils/businessCosts';
import { useAuth } from '../contexts/AuthContext';
import Lightbox from '../components/Lightbox/Lightbox';
import ReceiptThumbs from '../components/ReceiptThumbs';
import ReceiptForm from '../components/ReceiptForm';
import './BusinessCosts.css';

const OH_KEYS = ['overhead_basis', 'overhead_pool', 'overhead_budget_annual', 'planned_billable_hours', 'expected_annual_revenue'];
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// The Expenses table's period picker (backlog #686) — its own list since it needs "All time" and
// plain calendar years, which the Reports page's PERIODS (utils/businessCosts.js) does not offer.
const PURCHASE_PERIODS = [
  { value: 'all', label: 'All time' },
  { value: 'this_fy', label: 'This financial year' },
  { value: 'last_fy', label: 'Last financial year' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' }
];
const purchaseYears = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2, y - 3]; };

const blankExpense = () => ({ id: null, description: '', category: 'consumables', amount: '', incurred_on: todayBrisbane(), every_months: '', ends_on: '', vendor: '', equipment_id: '', paid_by: '', includes_gst: true, notes: '', job_id: '', job_category_id: '' });
const expenseForm = (e) => ({
  id: e.id, description: e.description, category: e.category, amount: String(e.amount), incurred_on: e.incurred_on,
  every_months: e.every_months ? String(e.every_months) : '', ends_on: e.ends_on || '', vendor: e.vendor || '',
  equipment_id: e.equipment_id ? String(e.equipment_id) : '', paid_by: e.paid_by ? String(e.paid_by) : '', includes_gst: !!e.includes_gst, notes: e.notes || '',
  job_id: '', job_category_id: '' // editing an expense never picks a job — that only happens when adding new (see ExpenseForm)
});
const blankEquipment = () => ({ id: null, name: '', bought_on: todayBrisbane(), cost: '', salvage_value: '0', life_basis: 'hours', life_hours: '', life_years: '', is_active: true, paid_by: '', notes: '' });
const equipmentForm = (e) => ({
  id: e.id, name: e.name, bought_on: e.bought_on || '', cost: String(e.cost), salvage_value: String(e.salvage_value), life_basis: e.life_basis,
  life_hours: e.life_hours ?? '', life_years: e.life_years ?? '', is_active: !!e.is_active, paid_by: e.paid_by ? String(e.paid_by) : '', notes: e.notes || ''
});
// Loans — cash only (GID-Session-016)
const blankLoan = () => ({ id: null, lender: '', purpose: '', equipment_id: '', principal: '', interest_rate_pct: '0', started_on: todayBrisbane(), repayment_amount: '', every_months: '1', is_active: true, notes: '' });
const loanForm = (l) => ({
  id: l.id, lender: l.lender, purpose: l.purpose || '', equipment_id: l.equipment_id ? String(l.equipment_id) : '', principal: String(l.principal),
  interest_rate_pct: String(l.interest_rate_pct), started_on: l.started_on || '', repayment_amount: l.repayment_amount === null ? '' : String(l.repayment_amount),
  every_months: l.every_months ? String(l.every_months) : '', is_active: !!l.is_active, notes: l.notes || ''
});
const blankPayment = (loan) => ({ loan_id: loan.id, paid_on: todayBrisbane(), amount: loan.repayment_amount ? String(loan.repayment_amount) : '', interest_part: '', notes: '' });

/**
 * Business costs (backlog #681, part 1): the costs that are not one job's —
 * expenses (one-off and recurring, with receipts), the equipment register
 * with depreciation — and the overhead RATE they make, which every job
 * carries on its P&L as an Overheads tile. Internal only.
 */
export default function BusinessCosts() {
  const { user } = useAuth(); // a new expense defaults its payer to whoever is logged in (backlog #685)
  const [data, setData] = useState(null); // { expenses, equipment, overhead, ledger }
  const [staff, setStaff] = useState([]); // the team, for "Paid by" / settlement pickers
  const [oh, setOh] = useState(null);     // the overhead settings being edited
  const [ex, setEx] = useState(null);     // expense form
  const [eq, setEq] = useState(null);     // equipment form
  const [ln, setLn] = useState(null);     // loan form
  const [pay, setPay] = useState(null);   // payment form { loan_id, paid_on, amount, interest_part, notes }
  const [openLoan, setOpenLoan] = useState(null); // the loan whose payments are open
  const [suggestion, setSuggestion] = useState(null); // the interest suggestion for the open payment form (GID-Session-017)
  const [touchedInterest, setTouchedInterest] = useState(false); // has the user typed in "Of which interest" themselves?
  const [openRepairs, setOpenRepairs] = useState(null); // the equipment whose repair expenses are shown
  const [settle, setSettle] = useState(null); // the "Record a payment" form { from_staff_id, to_staff_id, amount, paid_on, notes } (backlog #685)
  const [lightbox, setLightbox] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rf, setRf] = useState(false);    // "Add from a receipt" open (backlog #687)
  const [defaultPayer, setDefaultPayer] = useState(''); // Settings › "Who usually pays?" (backlog #688); '' = whoever is logged in
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // Expenses table: business expenses + job cost lines together (GID-Session-017, backlog #686)
  const [purchases, setPurchases] = useState(null); // { rows, totals, options } from /api/purchases
  const [purchasePeriod, setPurchasePeriod] = useState('this_fy');
  const [purchaseFilters, setPurchaseFilters] = useState({ category: '', payer: '', job: '', q: '' });
  const [purchaseSort, setPurchaseSort] = useState({ key: 'date', dir: 'desc' });
  const [jobs, setJobs] = useState([]); // active jobs, for the "For job" pickers
  const [jobCostCategories, setJobCostCategories] = useState([]); // active job cost categories, excluding labour/equipment

  const purchaseParams = (period) => {
    if (period === 'all') return { preset: 'all' };
    if (/^\d{4}$/.test(period)) return { from: `${period}-01-01`, to: `${period}-12-31` };
    return { preset: period };
  };
  // costs.csv doesn't understand preset=all, so "All time" needs an explicit huge range for the download too.
  const csvParamsFor = (period) => {
    if (period !== 'all') return purchaseParams(period);
    const [y, m, d] = todayBrisbane().split('-').map(Number);
    const far = new Date(Date.UTC(y, m - 1, d));
    far.setUTCDate(far.getUTCDate() + 3650);
    return { from: '2000-01-01', to: far.toISOString().slice(0, 10) };
  };
  async function loadPurchases(period = purchasePeriod) {
    const r = await businessCostsService.purchases(purchaseParams(period));
    if (r.success) setPurchases(r); else setError(r.error || 'Could not load the expenses table');
  }

  async function load() {
    const [r, s, st, jb, jc] = await Promise.all([businessCostsService.overview(), settingsService.get(), staffService.getAll(), jobsService.getAll(), costsService.categories({ active: 1 })]);
    if (!r.success) { setError(r.error || 'Could not load business costs'); return; }
    setData(r);
    if (s.success) {
      setOh(Object.fromEntries(OH_KEYS.map((k) => [k, s.settings?.[k] ?? ''])));
      setDefaultPayer(s.settings?.default_payer ? String(s.settings.default_payer) : '');
    }
    if (st.success) setStaff(st.staff || []);
    if (jb.success) setJobs((jb.jobs || []).filter((j) => j.status !== 'cancelled'));
    if (jc.success) setJobCostCategories((jc.categories || []).filter((c) => c.kind !== 'labour' && c.kind !== 'equipment'));
    await loadPurchases();
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { if (data) loadPurchases(); }, [purchasePeriod]); // eslint-disable-line react-hooks/exhaustive-deps
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };
  // Every "Paid by" starts as the usual payer (Settings, backlog #688), else whoever is logged in (backlog #685)
  const usualPayer = () => defaultPayer || (user?.id ? String(user.id) : '');
  const setO = (k, v) => setOh((p) => ({ ...p, [k]: v }));
  const setLedger = (ledger) => setData((d) => ({ ...d, ledger }));

  // ---- The overhead rate ----
  async function saveOverheads() {
    if (busy) return;
    setBusy(true); setError('');
    const r = await settingsService.update(oh);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the overhead settings'); return; }
    flash('Saved');
    load();
  }

  // ---- Expenses ----
  const setX = (k, v) => setEx((p) => ({ ...p, [k]: v }));
  async function saveExpense() {
    if (!ex || busy) return;
    if (!ex.description.trim()) { setError('Describe the expense'); return; }
    if (!(Number(ex.amount) >= 0) || ex.amount === '') { setError('Enter the amount'); return; }
    setBusy(true); setError('');
    let r;
    if (!ex.id && ex.job_id) {
      // "For a specific job — costs and consumables should be shown in expenses" (backlog #686):
      // a job is chosen, so this is a job cost line, not a business expense.
      r = await costsService.addJobLine(ex.job_id, {
        category_id: ex.job_category_id || null, description: ex.description, qty: 1, unit: 'ea',
        unit_cost: Number(ex.amount), incurred_on: ex.incurred_on, vendor: ex.vendor,
        includes_gst: ex.includes_gst, paid_by: ex.paid_by || null, notes: ex.notes
      });
    } else {
      const payload = {
        description: ex.description, category: ex.category, amount: Number(ex.amount), incurred_on: ex.incurred_on,
        every_months: ex.every_months === '' ? null : Number(ex.every_months), ends_on: ex.every_months === '' ? null : (ex.ends_on || null),
        vendor: ex.vendor, equipment_id: ex.equipment_id || null, paid_by: ex.paid_by || null, includes_gst: ex.includes_gst, notes: ex.notes
      };
      r = ex.id ? await businessCostsService.updateExpense(ex.id, payload) : await businessCostsService.addExpense(payload);
    }
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the expense'); return; }
    setEx(null);
    load();
  }
  // The full expense record (incurred_on / every_months / ends_on as stored, not the flattened
  // instalment row shown in the table) — editing always works from this, never from a purchases row.
  function openEditExpenseRow(r) {
    const full = data.expenses.find((e) => e.id === r.expense_id);
    if (!full) return;
    setError('');
    setEx(expenseForm(full));
  }
  async function removePurchaseRow(r) {
    const label = r.instalment ? `${r.description} — the whole recurring bill` : r.description;
    const others = r.receipt ? r.receipt.line_count - 1 : 0;
    const receiptNote = r.receipt ? (others > 0 ? ' Its receipt stays with the other lines on it.' : ' Its receipt photo goes with it.') : '';
    if (!window.confirm(`Remove "${label}"?${receiptNote}`)) return;
    const res = r.source === 'expense' ? await businessCostsService.deleteExpense(r.expense_id) : await costsService.deleteLine(r.line_id);
    if (res.success) load(); else setError(res.error || 'Could not remove it');
  }
  // "Add from a receipt" saved: the docket and every line on it are in (backlog #687)
  function receiptSaved(r) {
    setRf(false);
    const n = r.receipt?.line_count || 0;
    flash(`Receipt saved — ${n} line${n === 1 ? '' : 's'}${r.receipt?.vendor ? ` from ${r.receipt.vendor}` : ''}`);
    load();
  }
  function togglePurchaseSort(key) {
    setPurchaseSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  // ---- Equipment ----
  const setQ = (k, v) => setEq((p) => ({ ...p, [k]: v }));
  async function saveEquipment() {
    if (!eq || busy) return;
    if (!eq.name.trim()) { setError('Name the equipment'); return; }
    if (eq.cost === '' || !(Number(eq.cost) >= 0)) { setError('Enter what it cost'); return; }
    if (eq.life_basis === 'hours' && !(Number(eq.life_hours) > 0)) { setError('How many hours of use will it give?'); return; }
    if (eq.life_basis === 'years' && !(Number(eq.life_years) > 0)) { setError('How many years will it last?'); return; }
    setBusy(true); setError('');
    const payload = {
      name: eq.name, bought_on: eq.bought_on || null, cost: Number(eq.cost), salvage_value: Number(eq.salvage_value) || 0, life_basis: eq.life_basis,
      life_hours: eq.life_hours === '' ? null : Number(eq.life_hours), life_years: eq.life_years === '' ? null : Number(eq.life_years), is_active: eq.is_active, notes: eq.notes,
      paid_by: eq.paid_by || null
    };
    const r = eq.id ? await businessCostsService.updateEquipment(eq.id, payload) : await businessCostsService.addEquipment(payload);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the equipment'); return; }
    setEq(null);
    load();
  }
  async function removeEquipment(e) {
    if (!window.confirm(`Remove "${e.name}" from the register? Cost lines that named it keep their figures.`)) return;
    const r = await businessCostsService.deleteEquipment(e.id);
    if (r.success) load(); else setError(r.error || 'Could not remove the equipment');
  }

  // ---- Loans (cash only) ----
  const setL = (k, v) => setLn((p) => ({ ...p, [k]: v }));
  async function saveLoan() {
    if (!ln || busy) return;
    if (!ln.lender.trim()) { setError('Who lent the money?'); return; }
    if (ln.principal === '' || !(Number(ln.principal) >= 0)) { setError('How much was borrowed?'); return; }
    setBusy(true); setError('');
    const payload = {
      lender: ln.lender, purpose: ln.purpose, equipment_id: ln.equipment_id || null, principal: Number(ln.principal), interest_rate_pct: Number(ln.interest_rate_pct) || 0,
      started_on: ln.started_on || null, repayment_amount: ln.repayment_amount === '' ? null : Number(ln.repayment_amount), every_months: ln.every_months === '' ? null : Number(ln.every_months),
      is_active: ln.is_active, notes: ln.notes
    };
    const r = ln.id ? await businessCostsService.updateLoan(ln.id, payload) : await businessCostsService.addLoan(payload);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the loan'); return; }
    setLn(null);
    load();
  }
  async function removeLoan(l) {
    if (!window.confirm(`Remove the loan from ${l.lender}${l.payments.length ? ' and its payments' : ''}?`)) return;
    const r = await businessCostsService.deleteLoan(l.id);
    if (r.success) load(); else setError(r.error || 'Could not remove the loan');
  }
  const setP = (k, v) => setPay((p) => ({ ...p, [k]: v }));
  // Suggest the interest slice as paid_on / amount change, debounced — pre-fills
  // "Of which interest" only while the user has not typed in it themselves.
  useEffect(() => {
    if (!pay) { setSuggestion(null); return undefined; }
    const t = setTimeout(async () => {
      const params = { paid_on: pay.paid_on };
      if (pay.amount !== '') params.amount = pay.amount;
      const r = await businessCostsService.suggestInterest(pay.loan_id, params);
      if (r.success) {
        setSuggestion(r.suggestion);
        if (!touchedInterest) setP('interest_part', String(r.suggestion.interest_part));
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay?.loan_id, pay?.paid_on, pay?.amount]);
  async function savePayment() {
    if (!pay || busy) return;
    if (!(Number(pay.amount) > 0)) { setError('How much was paid?'); return; }
    setBusy(true); setError('');
    const r = await businessCostsService.addLoanPayment(pay.loan_id, { paid_on: pay.paid_on, amount: Number(pay.amount), interest_part: pay.interest_part === '' ? 0 : Number(pay.interest_part), notes: pay.notes });
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not record the payment'); return; }
    setOpenLoan(pay.loan_id);
    setPay(null);
    load();
  }
  async function removePayment(p) {
    if (!window.confirm(`Remove the ${formatMoney(p.amount)} payment on ${fmtDate(p.paid_on)}?`)) return;
    const r = await businessCostsService.deleteLoanPayment(p.id);
    if (r.success) load(); else setError(r.error || 'Could not remove the payment');
  }

  // ---- Between us: who paid, who owes whom (backlog #685) ----
  async function saveShare(staffId, val) {
    const pct = val === '' ? null : Number(val);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) { setError('A share is 0 to 100%'); return; }
    setError('');
    const r = await businessCostsService.saveShares({ [staffId]: pct });
    if (!r.success) { setError(r.error || 'Could not save the share'); return; }
    setLedger(r.ledger);
  }
  async function splitEvenly(activeStaff) {
    if (busy || activeStaff.length < 2) return;
    const base = Math.floor((100 / activeStaff.length) * 100) / 100;
    let used = 0;
    const shares = {};
    activeStaff.forEach((s, i) => {
      const pct = i === activeStaff.length - 1 ? round2(100 - used) : base;
      shares[s.id] = pct;
      used = round2(used + pct);
    });
    setBusy(true); setError('');
    const r = await businessCostsService.saveShares(shares);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not set the shares'); return; }
    setLedger(r.ledger);
  }
  function openSettle(ledger, activeStaff) {
    setError('');
    const w = ledger.who_owes?.[0];
    setSettle({
      from_staff_id: w ? String(w.from_staff_id) : (activeStaff[0] ? String(activeStaff[0].id) : ''),
      to_staff_id: w ? String(w.to_staff_id) : (activeStaff[1] ? String(activeStaff[1].id) : ''),
      amount: w ? String(w.amount) : '',
      paid_on: todayBrisbane(),
      notes: ''
    });
  }
  const setS = (k, v) => setSettle((p) => ({ ...p, [k]: v }));
  async function saveSettlement() {
    if (!settle || busy) return;
    if (!settle.from_staff_id || !settle.to_staff_id) { setError('Choose who paid and who received it'); return; }
    if (settle.from_staff_id === settle.to_staff_id) { setError('Choose two different people'); return; }
    if (!(Number(settle.amount) > 0)) { setError('Enter how much was paid'); return; }
    setBusy(true); setError('');
    const r = await businessCostsService.addSettlement({
      from_staff_id: Number(settle.from_staff_id), to_staff_id: Number(settle.to_staff_id),
      amount: Number(settle.amount), paid_on: settle.paid_on, notes: settle.notes
    });
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not record the payment'); return; }
    setSettle(null);
    setLedger(r.ledger);
  }
  async function removeSettlement(s) {
    if (!window.confirm(`Remove the ${formatMoney(s.amount)} payment from ${s.from_name} to ${s.to_name}?`)) return;
    const r = await businessCostsService.deleteSettlement(s.id);
    if (r.success) setLedger(r.ledger); else setError(r.error || 'Could not remove the payment');
  }

  if (!data || !oh) return <div className="empty-state">{error ? <p className="text-error">{error}</p> : <div className="loading-spinner"></div>}</div>;

  const o = data.overhead;
  const ls = data.loans_summary || { open_count: 0, owed: 0, monthly_commitment: 0, next_due: null, overdue_count: 0 };
  const hoursBasis = (oh.overhead_basis || 'hours') !== 'revenue';
  const budgetMode = oh.overhead_pool === 'budget';
  const rateText = o.basis === 'hours'
    ? (o.rate_per_hour === null ? '—' : `${formatMoney(o.rate_per_hour)} per hour`)
    : (o.pct_of_revenue === null ? '—' : `${o.pct_of_revenue}% of revenue`);
  const workingText = o.basis === 'hours'
    ? `${formatMoney(o.pool)} a year ÷ ${o.planned_billable_hours ? `${fmtNum(o.planned_billable_hours)} billable hours` : '? hours'}`
    : `${formatMoney(o.pool)} a year ÷ ${o.expected_annual_revenue ? `${formatMoney(o.expected_annual_revenue)} expected revenue` : '? revenue'}`;
  const exPreview = ex ? annualised(ex.amount, ex.every_months) : 0;
  const eqPreview = eq ? equipmentRate(eq) : null;
  const hourlyKit = data.equipment.filter((e) => e.is_active && e.hourly_rate !== null);
  const activeStaff = staff.filter((s) => s.is_active);
  const ledger = data.ledger || { total_shared: 0, line_count: 0, partners: [], settlements: [], who_owes: [], words: '', shares_note: null };
  const hasAnyShare = ledger.partners.some((p) => p.share_pct > 0);

  // The Expenses table: business expenses + job cost lines together, filtered and sorted
  // client-side over the period's rows (GID-Session-017, backlog #686).
  const purchaseRows = purchases?.rows || [];
  const purchaseOptions = purchases?.options || { categories: [], payers: [], jobs: [] };
  const visiblePurchaseRows = purchaseRows.filter((r) => {
    if (purchaseFilters.category && r.category !== purchaseFilters.category) return false;
    if (purchaseFilters.payer === 'none' && r.paid_by) return false;
    if (purchaseFilters.payer && purchaseFilters.payer !== 'none' && String(r.paid_by || '') !== purchaseFilters.payer) return false;
    if (purchaseFilters.job === 'business' && r.job) return false;
    if (purchaseFilters.job && purchaseFilters.job !== 'business' && String(r.job?.id || '') !== purchaseFilters.job) return false;
    if (purchaseFilters.q) {
      const hay = [r.description, r.vendor, r.notes, r.equipment_name, r.job?.job_number, r.job?.title, r.job?.customer_name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(purchaseFilters.q.toLowerCase())) return false;
    }
    return true;
  });
  const purchaseSortVal = (r, key) => {
    if (key === 'date') return r.date;
    if (key === 'what') return (r.description || '').toLowerCase();
    if (key === 'category') return (r.category_label || '').toLowerCase();
    if (key === 'paid_by') return (r.paid_by_name || '').toLowerCase();
    if (key === 'amount') return Number(r.amount) || 0;
    return '';
  };
  const sortedPurchaseRows = [...visiblePurchaseRows].sort((a, b) => {
    const av = purchaseSortVal(a, purchaseSort.key);
    const bv = purchaseSortVal(b, purchaseSort.key);
    const cmp = av < bv ? -1 : av > bv ? 1 : (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
    return purchaseSort.dir === 'asc' ? cmp : -cmp;
  });
  const purchaseFooterTotal = sortedPurchaseRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const sortArrow = (key) => (purchaseSort.key === key ? (purchaseSort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  // Editing an expense replaces every row of that same bill in view with the one inline form,
  // rather than showing the (identical) form once per instalment row.
  const editedExpenseShown = new Set();
  const purchaseRowElements = sortedPurchaseRows.map((r) => {
    if (ex?.id && r.source === 'expense' && ex.id === r.expense_id) {
      if (editedExpenseShown.has(r.expense_id)) return null;
      editedExpenseShown.add(r.expense_id);
      return (
        <tr key={`edit-${r.expense_id}`}>
          <td colSpan={8}>
            <ExpenseForm form={ex} set={setX} equipment={data.equipment} staff={activeStaff} preview={exPreview} busy={busy} onSave={saveExpense} onCancel={() => setEx(null)} />
          </td>
        </tr>
      );
    }
    return (
      <tr key={r.key}>
        <td className="bc-table-date">{fmtDate(r.date)}</td>
        <td className="bc-table-what">
          <span className="bc-desc">{r.description}</span>
          {r.job && <span className="bc-table-meta text-muted"><Link to={`/jobs/${r.job.id}`}>{r.job.job_number}</Link>{r.job.customer_name ? ` · ${r.job.customer_name}` : ''}</span>}
          {r.instalment && <span className="text-muted bc-table-meta">instalment {r.instalment.n}</span>}
          {r.notes && <span className="text-muted bc-table-meta">{r.notes}</span>}
        </td>
        <td>{r.category_label}</td>
        <td>{r.paid_by_name || '—'}</td>
        <td>{r.vendor || '—'}</td>
        <td className="bc-table-num">{formatMoney(r.amount)}</td>
        <td className="bc-table-receipts">
          {/* The shared docket (backlog #687): its pages, add a page, use one already uploaded, take it off */}
          <ReceiptThumbs kind={r.source === 'expense' ? 'expense' : 'job_cost'} rowId={r.source === 'expense' ? r.expense_id : r.line_id} receipt={r.receipt} onChange={load} onLightbox={(images, index) => setLightbox({ images, index })} />
        </td>
        <td className="bc-table-actions">
          {r.source === 'expense' ? (
            <>
              <button type="button" className="bc-icon-btn" title="Edit" aria-label="Edit expense" onClick={() => openEditExpenseRow(r)}>✎</button>
              <button type="button" className="bc-icon-btn bc-remove" title="Remove" aria-label="Remove expense" onClick={() => removePurchaseRow(r)}>×</button>
            </>
          ) : (
            <>
              <Link className="bc-purch-tojob" to={`/jobs/${r.job.id}`} title="Open the job">→ job</Link>
              <button type="button" className="bc-icon-btn bc-remove" title="Remove" aria-label="Remove cost" onClick={() => removePurchaseRow(r)}>×</button>
            </>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className="bc-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Business costs</h1>
          <p className="text-muted bc-intro">Costs that are not one job's — hose fittings, a broom, insurance, the pressure washer wearing out. They make an overhead rate that every job carries. Internal only.</p>
        </div>
        <div className="flex items-center gap-sm">
          {msg && <span className="text-success">{msg}</span>}
        </div>
      </div>
      {error && <p className="form-error bc-error">{error}</p>}

      {/* ---- The overhead rate ---- */}
      <section className="card bc-section">
        <div className="bc-head">
          <h2 className="card-title">Overhead rate</h2>
          <button type="button" className="btn btn-sm btn-primary" onClick={saveOverheads} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
        <div className="bc-rate">
          <div className="bc-rate-big">
            <strong>{rateText}</strong>
            <span className="text-muted">= {workingText}</span>
            {o.problem && <span className="form-error">{o.problem}</span>}
          </div>
          <div className="bc-pool">
            <span className={`bc-pill ${!budgetMode ? 'on' : ''}`}><em>One-off, last 12 months</em><strong>{formatMoney(o.actual.one_off_12m)}</strong></span>
            <span className={`bc-pill ${!budgetMode ? 'on' : ''}`}><em>Recurring bills, a year{o.actual.recurring_count ? ` (${o.actual.recurring_count})` : ''}</em><strong>{formatMoney(o.actual.recurring_annual)}</strong></span>
            <span className={`bc-pill ${!budgetMode ? 'on' : ''}`}><em>Equipment wearing out, a year{o.actual.equipment_count ? ` (${o.actual.equipment_count})` : ''}</em><strong>{formatMoney(o.actual.equipment_annual)}</strong></span>
            <span className={`bc-pill ${!budgetMode ? 'on' : ''}`}><em>Loan interest, a year{o.actual.loan_count ? ` (${o.actual.loan_count})` : ''}</em><strong>{formatMoney(o.actual.loan_interest_annual || 0)}</strong></span>
            <span className={`bc-pill bc-pill-total ${!budgetMode ? 'on' : ''}`}><em>Actual costs, a year</em><strong>{formatMoney(o.actual.total)}</strong></span>
            {budgetMode && <span className="bc-pill bc-pill-total on"><em>Budget, a year</em><strong>{formatMoney(o.budget_annual)}</strong></span>}
          </div>
        </div>
        <div className="bc-oh-form">
          <label>Spread overheads by
            <select value={hoursBasis ? 'hours' : 'revenue'} onChange={(e) => setO('overhead_basis', e.target.value)}>
              <option value="hours">Hours worked ($ per hour)</option>
              <option value="revenue">Revenue (% of each job)</option>
            </select>
          </label>
          {hoursBasis ? (
            <label>Planned billable hours a year<input type="number" min="0" step="10" value={oh.planned_billable_hours} onChange={(e) => setO('planned_billable_hours', e.target.value)} placeholder="1000" /></label>
          ) : (
            <label>Expected revenue a year ($)<input type="number" min="0" step="1000" value={oh.expected_annual_revenue} onChange={(e) => setO('expected_annual_revenue', e.target.value)} placeholder="e.g. 60000" /></label>
          )}
          <label>Overheads to spread
            <select value={budgetMode ? 'budget' : 'actual'} onChange={(e) => setO('overhead_pool', e.target.value)}>
              <option value="actual">The actual costs logged here</option>
              <option value="budget">A yearly budget</option>
            </select>
          </label>
          {budgetMode && <label>Budget a year ($)<input type="number" min="0" step="100" value={oh.overhead_budget_annual} onChange={(e) => setO('overhead_budget_annual', e.target.value)} placeholder={String(o.actual.total)} /></label>}
        </div>
        <p className="text-muted bc-hint">
          Every job's P&L shows its share as an Overheads tile — {o.basis === 'hours' ? 'the hours booked × this rate' : 'this % of what was quoted'} — and the profit after it. A job that is unusual can carry its own % instead, set on the job view.
          Management costing, not tax: the ATO's instant asset write-off and depreciation rules are a different calculation.
        </p>
      </section>

      {/* ---- Expenses: business expenses + job cost lines together (GID-Session-017, backlog #686) ---- */}
      <section className="card bc-section">
        <div className="bc-head">
          <h2 className="card-title">Expenses ({sortedPurchaseRows.length})</h2>
          <span className="bc-head-actions">
            {!rf && !ex && <button type="button" className="btn btn-sm btn-primary" onClick={() => { setError(''); setRf(true); }}>+ Add from a receipt</button>}
            {!ex && !rf && <button type="button" className="btn btn-sm btn-outline" onClick={() => { setError(''); setEx({ ...blankExpense(), paid_by: usualPayer() }); }}>+ Add expense</button>}
          </span>
        </div>
        <p className="text-muted bc-sub">Everything bought — for the business and for particular jobs. A recurring bill shows once per instalment. Fuel, materials and consumables for a job are on the job too: add them here with the job chosen, or from the job's Costs panel. One docket with several things on it: <strong>Add from a receipt</strong> — the photo once, every item as its own line.</p>
        {rf && <ReceiptForm staff={activeStaff} jobs={jobs} jobCategories={jobCostCategories} defaultPaidBy={usualPayer()} onSaved={receiptSaved} onCancel={() => setRf(false)} />}
        <div className="bc-filters">
          <select aria-label="Period" value={purchasePeriod} onChange={(e) => setPurchasePeriod(e.target.value)}>
            {PURCHASE_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            {purchaseYears().map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </select>
          <select aria-label="Filter category" value={purchaseFilters.category} onChange={(e) => setPurchaseFilters((f) => ({ ...f, category: e.target.value }))}>
            <option value="">All categories</option>
            {purchaseOptions.categories.map((c) => <option key={`${c.source}-${c.value}`} value={c.value}>{c.label}</option>)}
          </select>
          <select aria-label="Filter payer" value={purchaseFilters.payer} onChange={(e) => setPurchaseFilters((f) => ({ ...f, payer: e.target.value }))}>
            <option value="">All payers</option>
            <option value="none">Nobody</option>
            {purchaseOptions.payers.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
          <select aria-label="Filter job" value={purchaseFilters.job} onChange={(e) => setPurchaseFilters((f) => ({ ...f, job: e.target.value }))}>
            <option value="">All</option>
            <option value="business">Business only</option>
            {purchaseOptions.jobs.map((j) => <option key={j.id} value={String(j.id)}>{`${j.job_number}${j.title ? ` · ${j.title}` : ''}`}</option>)}
          </select>
          <input aria-label="Search expenses" type="search" placeholder="Search expenses…" value={purchaseFilters.q} onChange={(e) => setPurchaseFilters((f) => ({ ...f, q: e.target.value }))} />
          <a className="btn btn-sm btn-outline" href={reportsService.costsCsvUrl(csvParamsFor(purchasePeriod))} download>Download CSV</a>
        </div>
        {!purchases && <div className="empty-state"><div className="loading-spinner"></div></div>}
        {purchases && (
          <div className="bc-table-wrap">
            <table className="bc-table">
              <thead>
                <tr>
                  <th><button type="button" className="bc-table-sort" onClick={() => togglePurchaseSort('date')}>Date{sortArrow('date')}</button></th>
                  <th><button type="button" className="bc-table-sort" onClick={() => togglePurchaseSort('what')}>What{sortArrow('what')}</button></th>
                  <th><button type="button" className="bc-table-sort" onClick={() => togglePurchaseSort('category')}>Category{sortArrow('category')}</button></th>
                  <th><button type="button" className="bc-table-sort" onClick={() => togglePurchaseSort('paid_by')}>Paid by{sortArrow('paid_by')}</button></th>
                  <th>Supplier</th>
                  <th className="bc-table-num"><button type="button" className="bc-table-sort" onClick={() => togglePurchaseSort('amount')}>Amount{sortArrow('amount')}</button></th>
                  <th>Receipts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{purchaseRowElements}</tbody>
              <tfoot>
                <tr><td colSpan={8}>{sortedPurchaseRows.length} row{sortedPurchaseRows.length === 1 ? '' : 's'} · {formatMoney(purchaseFooterTotal)}</td></tr>
              </tfoot>
            </table>
            {sortedPurchaseRows.length === 0 && <p className="text-muted bc-hint">{purchaseRows.length ? 'Nothing matches these filters.' : 'Nothing logged yet.'}</p>}
          </div>
        )}
        {ex && !ex.id && <ExpenseForm form={ex} set={setX} equipment={data.equipment} staff={activeStaff} jobs={jobs} jobCategories={jobCostCategories} preview={exPreview} busy={busy} onSave={saveExpense} onCancel={() => setEx(null)} />}
      </section>

      {/* ---- Equipment ---- */}
      <section className="card bc-section">
        <div className="bc-head">
          <h2 className="card-title">Equipment ({data.equipment.length})</h2>
          {!eq && <button type="button" className="btn btn-sm btn-outline" onClick={() => { setError(''); setEq({ ...blankEquipment(), paid_by: usualPayer() }); }}>+ Add equipment</button>}
        </div>
        <p className="text-muted bc-sub">
          Life in <strong>hours</strong>: the job's Equipment cost line pre-fills hours × the rate{hourlyKit.length ? ` (${hourlyKit.map((e) => `${e.name} ${formatMoney(e.hourly_rate)}/h`).join(', ')})` : ''}.
          Life in <strong>years</strong>: the yearly depreciation goes into overheads instead, so nothing is counted twice.
          A loan on a piece of equipment is a cash matter — its repayments are not another cost.
        </p>
        {data.equipment.length === 0 && !eq && <p className="text-muted bc-hint">Nothing on the register yet.</p>}
        {data.equipment.length > 0 && (
          <div className="bc-lines">
            {data.equipment.map((e) => (eq?.id === e.id ? (
              <EquipmentForm key={e.id} form={eq} set={setQ} staff={activeStaff} preview={eqPreview} busy={busy} onSave={saveEquipment} onCancel={() => setEq(null)} />
            ) : (
              <div key={e.id} className="bc-equipment-item">
                <div className={`bc-line ${e.is_active ? '' : 'bc-ended'}`}>
                  <span className="bc-main">
                    <span className="bc-desc">{e.name}{!e.is_active && <span className="status-badge bc-badge">retired</span>}</span>
                    <span className="bc-meta text-muted">
                      {e.bought_on ? `Bought ${fmtDate(e.bought_on)} · ` : ''}{formatMoney(e.cost)}{e.salvage_value ? ` less ${formatMoney(e.salvage_value)} at the end` : ''}
                      {e.life_basis === 'years' ? ` · lasts ${fmtNum(e.life_years)} year${Number(e.life_years) === 1 ? '' : 's'}${e.life_end ? ` (to ${fmtDate(e.life_end)})` : ''}` : ` · ${fmtNum(e.life_hours)} hours of use`}
                      {e.lines_logged ? ` · ${fmtNum(e.hours_logged)} h on ${e.lines_logged} job line${e.lines_logged === 1 ? '' : 's'}${e.life_used_pct !== null ? ` (${fmtNum(e.life_used_pct, 0)}% of its life)` : ''}` : ''}
                      {e.repairs_count ? ` · ${e.repairs_count} repair/service expense${e.repairs_count === 1 ? '' : 's'}, ${formatMoney(e.repairs_total)}` : ''}
                      {e.paid_by_name ? ` · paid by ${e.paid_by_name}` : ''}
                      {e.notes ? ` · ${e.notes}` : ''}
                    </span>
                    {/* Equipment carries a receipt too (backlog #687) — the same docket as the other things bought with it */}
                    <span className="bc-receipts">
                      <ReceiptThumbs kind="equipment" rowId={e.id} receipt={e.receipt} onChange={load} onLightbox={(images, index) => setLightbox({ images, index })} />
                    </span>
                  </span>
                  <span className="bc-amount">
                    {e.hourly_rate !== null ? <>{formatMoney(e.hourly_rate)}<em className="text-muted">/h</em></> : e.yearly_depreciation !== null ? <>{formatMoney(e.yearly_depreciation)}<em className="text-muted">/yr{e.depreciating ? '' : ' · done'}</em></> : '—'}
                  </span>
                  <span className="bc-actions">
                    {e.repairs_count > 0 && <button type="button" className="bc-icon-btn" title={openRepairs === e.id ? 'Hide repairs' : 'Show repairs'} aria-label="Repairs" onClick={() => setOpenRepairs(openRepairs === e.id ? null : e.id)}>{openRepairs === e.id ? '▴' : '▾'}</button>}
                    <button type="button" className="bc-icon-btn" title="Edit" aria-label="Edit equipment" onClick={() => { setError(''); setEq(equipmentForm(e)); }}>✎</button>
                    <button type="button" className="bc-icon-btn bc-remove" title="Remove" aria-label="Remove equipment" onClick={() => removeEquipment(e)}>×</button>
                  </span>
                </div>
                {openRepairs === e.id && (
                  <div className="bc-payments">
                    {data.expenses.filter((x) => x.equipment_id === e.id).map((x) => (
                      <div key={x.id} className="bc-payment">
                        <span className="bc-date">{fmtDate(x.incurred_on)}</span>
                        <span className="bc-main"><span>{x.description}{x.amount ? ` — ${formatMoney(x.amount)}` : ''}<em className="text-muted"> · {categoryLabel(x.category)}</em></span></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )))}
          </div>
        )}
        {eq && !eq.id && <EquipmentForm form={eq} set={setQ} staff={activeStaff} preview={eqPreview} busy={busy} onSave={saveEquipment} onCancel={() => setEq(null)} />}
      </section>

      {/* ---- Loans (cash only) ---- */}
      <section className="card bc-section">
        <div className="bc-head">
          <h2 className="card-title">Loans ({(data.loans || []).length})</h2>
          {!ln && <button type="button" className="btn btn-sm btn-outline" onClick={() => { setError(''); setLn(blankLoan()); }}>+ Add loan</button>}
        </div>
        <p className="text-muted bc-sub">
          Cash only. A loan bought something that is already on the register, so its repayments are not another cost — only the interest is, and the yearly interest on what is still owed goes into overheads.
          {ls.open_count > 0 && <> <strong>Owed {formatMoney(ls.owed)}</strong> across {ls.open_count} loan{ls.open_count === 1 ? '' : 's'}{ls.monthly_commitment ? ` · ${formatMoney(ls.monthly_commitment)} a month in repayments` : ''}{ls.next_due ? ` · next due ${fmtDate(ls.next_due)}` : ''}{ls.overdue_count ? <span className="form-error"> · {ls.overdue_count} overdue</span> : null}.</>}
        </p>
        {(data.loans || []).length === 0 && !ln && <p className="text-muted bc-hint">No loans.</p>}
        {(data.loans || []).length > 0 && (
          <div className="bc-lines">
            {data.loans.map((l) => (ln?.id === l.id ? (
              <LoanForm key={l.id} form={ln} set={setL} equipment={data.equipment} busy={busy} onSave={saveLoan} onCancel={() => setLn(null)} />
            ) : (
              <div key={l.id} className={`bc-loan ${l.status !== 'open' ? 'bc-ended' : ''}`}>
                <div className="bc-line">
                  <span className="bc-main">
                    <span className="bc-desc">{l.lender}{l.purpose ? ` — ${l.purpose}` : ''}{l.status !== 'open' && <span className="status-badge bc-badge">{LOAN_STATUS[l.status]}</span>}{l.overdue && <span className="status-badge bc-badge bc-badge-warn">overdue</span>}</span>
                    <span className="bc-meta text-muted">
                      {formatMoney(l.principal)} borrowed{l.started_on ? ` ${fmtDate(l.started_on)}` : ''}{l.interest_rate_pct ? ` at ${l.interest_rate_pct}% a year` : ' · no interest'}
                      {l.repayment_amount ? ` · ${formatMoney(l.repayment_amount)} ${l.every_months ? everyLabel(l.every_months) : 'as and when'}` : ''}
                      {l.equipment_name ? ` · bought ${l.equipment_name}` : ''}
                      {l.paid_total ? ` · paid ${formatMoney(l.paid_total)}${l.interest_paid ? ` (${formatMoney(l.interest_paid)} of it interest)` : ''}` : ' · nothing paid yet'}
                      {l.next_due && l.status === 'open' ? ` · next due ${fmtDate(l.next_due)}` : ''}
                      {l.annual_interest ? ` · ${formatMoney(l.annual_interest)} a year interest → overheads` : ''}
                      {l.payoff_date ? ` · paid off by ${fmtDate(l.payoff_date)} (${l.instalments_left} instalment${l.instalments_left === 1 ? '' : 's'}, ${formatMoney(l.interest_remaining)} more interest)` : ''}
                      {l.payoff_problem ? ` · ${l.payoff_problem}` : ''}
                      {l.notes ? ` · ${l.notes}` : ''}
                    </span>
                  </span>
                  <span className="bc-amount">{formatMoney(l.balance)}<em className="text-muted"> owed</em></span>
                  <span className="bc-actions">
                    <button type="button" className="btn btn-sm btn-outline bc-pay-btn" onClick={() => { setError(''); setOpenLoan(l.id); setPay(blankPayment(l)); setSuggestion(null); setTouchedInterest(false); }} disabled={l.status === 'closed'}>+ Payment</button>
                    <button type="button" className="bc-icon-btn" title={openLoan === l.id ? 'Hide payments' : 'Show payments'} aria-label="Payments" onClick={() => setOpenLoan(openLoan === l.id ? null : l.id)}>{openLoan === l.id ? '▴' : '▾'}</button>
                    <button type="button" className="bc-icon-btn" title="Edit" aria-label="Edit loan" onClick={() => { setError(''); setLn(loanForm(l)); }}>✎</button>
                    <button type="button" className="bc-icon-btn bc-remove" title="Remove" aria-label="Remove loan" onClick={() => removeLoan(l)}>×</button>
                  </span>
                </div>
                {(openLoan === l.id || pay?.loan_id === l.id) && (
                  <div className="bc-payments">
                    {l.payments.length === 0 && !pay && <span className="text-muted">No payments recorded yet.</span>}
                    {l.payments.map((p) => (
                      <div key={p.id} className="bc-payment">
                        <span className="bc-date">{fmtDate(p.paid_on)}</span>
                        <span className="bc-main"><span>{formatMoney(p.amount)}{p.interest_part ? <em className="text-muted"> · {formatMoney(p.interest_part)} of it interest</em> : null}{p.notes ? <em className="text-muted"> · {p.notes}</em> : null}</span></span>
                        <button type="button" className="bc-icon-btn bc-remove" title="Remove payment" aria-label="Remove payment" onClick={() => removePayment(p)}>×</button>
                      </div>
                    ))}
                    {pay?.loan_id === l.id && (
                      <div className="bc-form bc-pay-form" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); savePayment(); } }}>
                        <div className="bc-form-row">
                          <label className="bc-inline-label">Paid on<input aria-label="Paid on" type="date" value={pay.paid_on} onChange={(e) => setP('paid_on', e.target.value)} /></label>
                          <label className="bc-inline-label">Amount<div className="bc-money"><span>$</span><input aria-label="Payment amount" type="number" min="0" step="0.01" value={pay.amount} onChange={(e) => setP('amount', e.target.value)} placeholder="0.00" /></div></label>
                          <label className="bc-inline-label" title="The slice of this payment that was interest — a cost. The rest pays the loan down and is not.">Of which interest<div className="bc-money"><span>$</span><input aria-label="Interest part" type="number" min="0" step="0.01" value={pay.interest_part} onChange={(e) => { setTouchedInterest(true); setP('interest_part', e.target.value); }} placeholder="0" /></div></label>
                          <input aria-label="Payment notes" placeholder="Notes (optional)" value={pay.notes} onChange={(e) => setP('notes', e.target.value)} />
                          <button type="button" className="btn btn-sm btn-outline" onClick={() => { setPay(null); setSuggestion(null); }} disabled={busy}>Cancel</button>
                          <button type="button" className="btn btn-sm btn-primary" onClick={savePayment} disabled={busy}>{busy ? 'Saving…' : 'Record payment'}</button>
                        </div>
                        {suggestion && (
                          <p className="text-muted bc-suggest-hint">
                            {suggestion.rate_pct === 0
                              ? 'No interest on this loan'
                              : `Suggested: ${formatMoney(suggestion.interest_part)} interest for ${suggestion.days} day${suggestion.days === 1 ? '' : 's'} at ${suggestion.rate_pct}% on ${formatMoney(suggestion.balance)}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )))}
          </div>
        )}
        {ln && !ln.id && <LoanForm form={ln} set={setL} equipment={data.equipment} busy={busy} onSave={saveLoan} onCancel={() => setLn(null)} />}
      </section>

      {/* ---- Between us: who paid, who owes whom (backlog #685) ---- */}
      <section className="card bc-section">
        <div className="bc-head">
          <h2 className="card-title">Between us</h2>
          {!settle && <button type="button" className="btn btn-sm btn-outline" onClick={() => openSettle(ledger, activeStaff)} disabled={activeStaff.length < 2}>+ Record a payment</button>}
        </div>
        <p className="text-muted bc-sub">Who paid what, and who owes whom. Only lines with a payer count — expenses, cash cost lines and equipment bought outright; wages and equipment wear are not cash.</p>
        {ledger.shares_note && <p className="text-muted bc-hint">{ledger.shares_note}</p>}
        {ledger.partners.length > 0 && (
          <div className="bc-ledger-table">
            <div className="bc-ledger-row bc-ledger-head text-muted">
              <span>Person</span><span>Share %</span><span>Paid</span><span>Their share</span><span>Balance</span>
            </div>
            {ledger.partners.map((p) => (
              <div key={p.staff_id} className="bc-ledger-row">
                <span className="bc-ledger-name">{p.name}</span>
                <span className="bc-ledger-share"><ShareInput person={p} onSave={saveShare} busy={busy} />%</span>
                <span>{formatMoney(p.paid)}</span>
                <span>{formatMoney(p.share)}</span>
                <span className={`bc-ledger-balance ${p.balance > 0.004 ? 'bc-ledger-owed' : p.balance < -0.004 ? 'bc-ledger-owes' : ''}`}>
                  {p.balance > 0.004 ? `${formatMoney(p.balance)} owed to them` : p.balance < -0.004 ? `${formatMoney(Math.abs(p.balance))} they owe` : '$0.00'}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="bc-ledger-words"><strong>{ledger.words || "Set each partner's share to start"}</strong></p>
        {!hasAnyShare && activeStaff.length > 1 && (
          <button type="button" className="btn btn-sm btn-outline" onClick={() => splitEvenly(activeStaff)} disabled={busy}>Split evenly</button>
        )}
        {settle && (
          <div className="bc-form bc-ledger-settle-form" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); saveSettlement(); } }}>
            <div className="bc-form-row">
              <label className="bc-inline-label">From
                <select aria-label="Paid by" value={settle.from_staff_id} onChange={(e) => setS('from_staff_id', e.target.value)}>
                  <option value="">Who paid?</option>
                  {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label className="bc-inline-label">To
                <select aria-label="Paid to" value={settle.to_staff_id} onChange={(e) => setS('to_staff_id', e.target.value)}>
                  <option value="">Who received it?</option>
                  {activeStaff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label className="bc-inline-label">Amount<div className="bc-money"><span>$</span><input aria-label="Settlement amount" type="number" min="0" step="0.01" value={settle.amount} onChange={(e) => setS('amount', e.target.value)} placeholder="0.00" /></div></label>
              <label className="bc-inline-label">Paid on<input aria-label="Settlement paid on" type="date" value={settle.paid_on} onChange={(e) => setS('paid_on', e.target.value)} /></label>
              <input aria-label="Settlement notes" placeholder="Notes (optional)" value={settle.notes} onChange={(e) => setS('notes', e.target.value)} />
            </div>
            <div className="bc-form-row bc-form-foot">
              <span className="spacer" />
              <button type="button" className="btn btn-sm btn-outline" onClick={() => setSettle(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={saveSettlement} disabled={busy}>{busy ? 'Saving…' : 'Record payment'}</button>
            </div>
          </div>
        )}
        {ledger.settlements.length === 0 && !settle && <p className="text-muted bc-hint">No payments recorded between you yet.</p>}
        {ledger.settlements.length > 0 && (
          <div className="bc-payments bc-ledger-settlements">
            {ledger.settlements.map((s) => (
              <div key={s.id} className="bc-payment">
                <span className="bc-date">{fmtDate(s.paid_on)}</span>
                <span className="bc-main"><span>{s.from_name} paid {s.to_name} {formatMoney(s.amount)}{s.notes ? <em className="text-muted"> · {s.notes}</em> : null}</span></span>
                <button type="button" className="bc-icon-btn bc-remove" title="Remove" aria-label="Remove settlement" onClick={() => removeSettlement(s)}>×</button>
              </div>
            ))}
          </div>
        )}
      </section>

      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function LoanForm({ form, set, equipment, busy, onSave, onCancel }) {
  return (
    <div className="bc-form" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onSave(); } }}>
      <div className="bc-form-row">
        <input aria-label="Lender" className="bc-desc-input" placeholder="Who lent it? e.g. Mum & Dad, the bank" value={form.lender} onChange={(e) => set('lender', e.target.value)} />
        <input aria-label="Purpose" className="bc-desc-input" placeholder="What for? e.g. Second pressure washer" value={form.purpose} onChange={(e) => set('purpose', e.target.value)} />
        <select aria-label="Bought which equipment" value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} title="The asset it bought — already on the register, so the repayments are not a cost">
          <option value="">Not tied to equipment</option>
          {equipment.map((e) => <option key={e.id} value={e.id}>Bought: {e.name}</option>)}
        </select>
      </div>
      <div className="bc-form-row">
        <label className="bc-inline-label">Borrowed<div className="bc-money"><span>$</span><input aria-label="Principal" type="number" min="0" step="0.01" value={form.principal} onChange={(e) => set('principal', e.target.value)} placeholder="0.00" /></div></label>
        <label className="bc-inline-label">Interest % a year<input aria-label="Interest rate" type="number" min="0" max="100" step="0.1" value={form.interest_rate_pct} onChange={(e) => set('interest_rate_pct', e.target.value)} placeholder="0" /></label>
        <label className="bc-inline-label">Started<input aria-label="Started on" type="date" value={form.started_on} onChange={(e) => set('started_on', e.target.value)} /></label>
        <label className="bc-inline-label">Repayment<div className="bc-money"><span>$</span><input aria-label="Repayment amount" type="number" min="0" step="0.01" value={form.repayment_amount} onChange={(e) => set('repayment_amount', e.target.value)} placeholder="0.00" /></div></label>
        <label className="bc-inline-label">Falls
          <select aria-label="Repayments every" value={form.every_months} onChange={(e) => set('every_months', e.target.value)}>
            <option value="">As and when</option>
            {EVERY_MONTHS.filter((o) => o.value !== '').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      </div>
      <div className="bc-form-row">
        <input aria-label="Loan notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
      <div className="bc-form-row bc-form-foot">
        <label className="checkbox-label bc-gst"><input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} /> Open (untick when closed or written off)</label>
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : (form.id ? 'Save' : 'Add loan')}</button>
      </div>
    </div>
  );
}

function ExpenseForm({ form, set, equipment, staff = [], jobs = [], jobCategories = [], preview, busy, onSave, onCancel }) {
  const recurring = form.every_months !== '';
  const cat = EXPENSE_CATEGORIES.find((c) => c.value === form.category);
  // Adding new can be "for a job" — costs and consumables for that job (backlog #686); editing
  // an existing expense is always a business expense, so the picker only shows up when adding.
  const forJob = !form.id && !!form.job_id;
  return (
    <div className="bc-form" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onSave(); } }}>
      {!form.id && (
        <div className="bc-form-row">
          <select aria-label="For job" value={form.job_id} onChange={(e) => set('job_id', e.target.value)} title="Fuel, materials and consumables for a job — choose it here to add it to that job's costs">
            <option value="">Business (not for a job)</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{`${j.job_number} · ${j.title || 'Untitled job'}${j.customer_name ? ` · ${j.customer_name}` : ''}`}</option>)}
          </select>
        </div>
      )}
      <div className="bc-form-row">
        {forJob ? (
          <select aria-label="Category" value={form.job_category_id} onChange={(e) => set('job_category_id', e.target.value)}>
            <option value="">Category…</option>
            {jobCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <select aria-label="Category" value={form.category} title={cat?.hint || ''} onChange={(e) => set('category', e.target.value)}>
            {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        )}
        <input aria-label="Description" className="bc-desc-input" placeholder="What was it?" value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div className="bc-form-row">
        <div className="bc-money"><span>$</span><input aria-label="Amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" /></div>
        {!forJob && (
          <select aria-label="Repeats" value={form.every_months} onChange={(e) => set('every_months', e.target.value)}>
            {EVERY_MONTHS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
        <label className="bc-inline-label">{recurring && !forJob ? 'First bill' : 'Date'}<input aria-label={recurring && !forJob ? 'First bill' : 'Date'} type="date" value={form.incurred_on} onChange={(e) => set('incurred_on', e.target.value)} /></label>
        {recurring && !forJob && <label className="bc-inline-label">Until (optional)<input aria-label="Last bill" type="date" value={form.ends_on} onChange={(e) => set('ends_on', e.target.value)} /></label>}
      </div>
      <div className="bc-form-row">
        <input aria-label="Supplier" placeholder="Supplier (optional)" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
        {!forJob && (
          <select aria-label="For which equipment" value={form.equipment_id} onChange={(e) => set('equipment_id', e.target.value)} title="A repair, service or part for a piece of equipment">
            <option value="">Not for a piece of equipment</option>
            {equipment.map((e) => <option key={e.id} value={e.id}>For: {e.name}</option>)}
          </select>
        )}
        <select aria-label="Paid by" value={form.paid_by} onChange={(e) => set('paid_by', e.target.value)} title="Who fronted the cash — feeds the Between us ledger">
          <option value="">Paid by… (optional)</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input aria-label="Notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
      <div className="bc-form-row bc-form-foot">
        <label className="checkbox-label bc-gst"><input type="checkbox" checked={!!form.includes_gst} onChange={(e) => set('includes_gst', e.target.checked)} /> Amount includes GST</label>
        {recurring && !forJob && <span className="text-muted bc-preview">= {formatMoney(preview)} a year</span>}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : (form.id ? 'Save' : 'Add expense')}</button>
      </div>
    </div>
  );
}

function EquipmentForm({ form, set, staff = [], preview, busy, onSave, onCancel }) {
  const years = form.life_basis === 'years';
  const basis = LIFE_BASES.find((b) => b.value === form.life_basis);
  return (
    <div className="bc-form" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onSave(); } }}>
      <div className="bc-form-row">
        <input aria-label="Equipment name" className="bc-desc-input" placeholder="e.g. Pressure washer — Kärcher HD 7/16" value={form.name} onChange={(e) => set('name', e.target.value)} />
        <label className="bc-inline-label">Bought<input aria-label="Bought on" type="date" value={form.bought_on} onChange={(e) => set('bought_on', e.target.value)} /></label>
      </div>
      <div className="bc-form-row">
        <label className="bc-inline-label">Cost<div className="bc-money"><span>$</span><input aria-label="Cost" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => set('cost', e.target.value)} placeholder="0.00" /></div></label>
        <label className="bc-inline-label" title="What it will be worth at the end of its life (0 = nothing)">Worth at the end<div className="bc-money"><span>$</span><input aria-label="Salvage value" type="number" min="0" step="0.01" value={form.salvage_value} onChange={(e) => set('salvage_value', e.target.value)} placeholder="0" /></div></label>
        <label className="bc-inline-label">Life measured in
          <select aria-label="Life measured in" value={form.life_basis} title={basis?.hint || ''} onChange={(e) => set('life_basis', e.target.value)}>
            {LIFE_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>
        {years
          ? <label className="bc-inline-label">Years<input aria-label="Life in years" type="number" min="0" step="0.5" value={form.life_years} onChange={(e) => set('life_years', e.target.value)} placeholder="e.g. 5" /></label>
          : <label className="bc-inline-label">Hours of use<input aria-label="Life in hours" type="number" min="0" step="10" value={form.life_hours} onChange={(e) => set('life_hours', e.target.value)} placeholder="e.g. 1000" /></label>}
      </div>
      <div className="bc-form-row">
        <select aria-label="Paid by" value={form.paid_by || ''} onChange={(e) => set('paid_by', e.target.value)} title="Who fronted the purchase — feeds the Between us ledger. Leave blank if a loan paid for it: the repayments are the cash then.">
          <option value="">Paid by… (blank if a loan paid for it)</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input aria-label="Notes" placeholder="Notes (optional) — serial number, where the loan is, …" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
      <div className="bc-form-row bc-form-foot">
        <label className="checkbox-label bc-gst"><input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} /> In use (untick when sold or retired)</label>
        {preview && (preview.hourly_rate !== null
          ? <span className="text-muted bc-preview">= {formatMoney(preview.hourly_rate)} per hour of use</span>
          : preview.yearly_depreciation !== null
            ? <span className="text-muted bc-preview">= {formatMoney(preview.yearly_depreciation)} a year into overheads</span>
            : null)}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : (form.id ? 'Save' : 'Add equipment')}</button>
      </div>
    </div>
  );
}

/** A share % that saves itself on blur/Enter (backlog #685) — a local draft so typing does not fight the server round-trip, resynced whenever the ledger sends back a fresh value. */
function ShareInput({ person, onSave, busy }) {
  const [val, setVal] = useState(String(person.share_pct));
  useEffect(() => { setVal(String(person.share_pct)); }, [person.share_pct]);
  const commit = () => { if (val !== String(person.share_pct)) onSave(person.staff_id, val); };
  return (
    <input
      aria-label={`Share % for ${person.name}`} type="number" min="0" max="100" step="0.1" value={val} disabled={busy}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); } }}
    />
  );
}
